import { useCallback } from 'react';
import useSignupData from '@assetron/common/hooks/useSignupData';
import AssetronOverlayDialog from '@assetron/web-common/components/common/AssetronOverlayDialog';
import SignupCreatePassword from 'auth/src/components/signup/SignupCreatePassword';
import HeaderFooterLayout from '@assetron/web-common/layout/HeaderFooterLayout';
import {
  API_ERROR_CODES,
  EMPTY_STRING,
} from '@assetron/common/constants/constant';
import { RESET_PASSWORD_OTP } from '@assetron/web-common/constants/LocalStorageConstants';
import { useLocalization } from '@assetron/common/hooks/useLocalization';
import { useNavigation } from '../../navigation/useNavigation';
import { useMutation } from '@tanstack/react-query';
import { CreateNewPassword } from '@assetron/common/api';
import { ResponseStatusCode } from '@assetron/common/types/responseStatus';
import usePasswordState from '../../hooks/usePasswordState';
import { useLocalStorage } from '@uidotdev/usehooks';
import useDocumentTitle from '@assetron/web-common/components/common/useDocumentTitle';
import { notifyToaster } from '@assetron/web-common/components/Toaster/ToasterService';
import apiErrorHandler from '@assetron/common/utils/apiErrorHandler';
import { GA_EVENTS } from '@assetron/common/constants/googleAnalyticsEvents';
import { trackGaEvent } from '@assetron/common/utils/googleAnalytics';

function ResetPasswordCreatePassword() {
  const { navigateToResetPasswordSuccess, navigateToSignInLanding } =
    useNavigation();
  const { clearInfo, userEmail, setUserNewPassword } = useSignupData();
  const { password, setPassword, isApiError, setIsApiError } =
    usePasswordState();
  const [resetPasswordOtp, saveResetPasswordOtp] = useLocalStorage(
    RESET_PASSWORD_OTP,
    EMPTY_STRING,
  );

  const code = resetPasswordOtp;
  const { t } = useLocalization();
  useDocumentTitle(t('resetPassword.pageTitle'));

  const createNewPasswordMutation = useMutation({
    mutationFn: CreateNewPassword,
    onSuccess: (res) => {
      if (res.status === ResponseStatusCode.NoContent) {
        trackGaEvent(GA_EVENTS.AUTH_RESET_PASSWORD_SUCCESS, {
          has_otp_code: Boolean(code),
        });
        saveResetPasswordOtp(EMPTY_STRING);
        setUserNewPassword(password);
        navigateToResetPasswordSuccess();
      } else {
        trackGaEvent(GA_EVENTS.AUTH_RESET_PASSWORD_FAILURE, {
          status_code: res.status,
        });
        notifyToaster({ type: 'error', message: t('apiErrorText') });
      }
    },
    onError: (error) => {
      trackGaEvent(GA_EVENTS.AUTH_RESET_PASSWORD_FAILURE, {
        status_code: error?.response?.status,
      });
      apiErrorHandler(
        error,
        {
          [API_ERROR_CODES.BAD_REQUEST]: t('authErrors.invalidInfo'),
        },
        t('apiErrorText'),
      );
    },
  });

  const handleClick = useCallback(() => {
    trackGaEvent(GA_EVENTS.AUTH_RESET_PASSWORD_SUBMIT, {
      password_length: password.length,
      has_otp_code: Boolean(code),
    });
    createNewPasswordMutation.mutate({
      email: userEmail ?? EMPTY_STRING,
      password,
      code: code ?? EMPTY_STRING,
    });
  }, [code, createNewPasswordMutation, password, userEmail]);

  const handleClose = useCallback((): void => {
    clearInfo();
    navigateToSignInLanding();
  }, [clearInfo, navigateToSignInLanding]);

  return (
    <HeaderFooterLayout>
      <AssetronOverlayDialog
        open
        isLogoVisible
        content={
          <SignupCreatePassword
            password={password}
            setPassword={setPassword}
            handleClick={handleClick}
            isApiError={isApiError}
            setIsApiError={setIsApiError}
            isLoading={createNewPasswordMutation.isLoading}
            showPolicyText={false}
            title={t('resetPassword.createNewPassword')}
            buttonText={t('resetPassword.resetPassword')}
          />
        }
        onClose={handleClose}
      />
    </HeaderFooterLayout>
  );
}

export default ResetPasswordCreatePassword;
